import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Title, Text, Grid, Card, Badge, TextInput, Button } from '@tremor/react';
import { Search, Footprints } from 'lucide-react';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, extractErrorMessage } from '@/lib/api';
import {
  formatRangeShort,
  useDateRange,
  useDateRangeKey,
} from '@/lib/dateRange';

interface FunnelStep {
  key: string;
  label: string;
  count: number;
  rate_from_previous: number | null;
  rate_from_top: number | null;
}

interface FunnelResponse {
  window: { start: string; end: string; days: number };
  as_of: string;
  // [ASSISTIDO 22/10] cadastros que NÓS abrimos e o cliente ainda não
  // acessou: ficam FORA de "Cadastros" (não são aquisição).
  assistidos?: number;
  steps: FunnelStep[];
}

interface JourneyEvent {
  id: number;
  event_type: string;
  event_name: string | null;
  page_url: string | null;
  page_title: string | null;
  properties: unknown;
  source: string | null;
  created_at: string | null;
}

interface JourneySession {
  session_id: string;
  started_at: string | null;
  device_type: string | null;
  browser: string | null;
  os: string | null;
  country: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  events: JourneyEvent[];
}

interface JourneyResponse {
  filter: { user_id: number | null; visitor_id: string | null; session_id: string | null };
  window: { start: string; end: string };
  total_events: number;
  events_returned: number;
  sessions: JourneySession[];
}

function fmtPct(n: number | null): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'medium',
  });
}

export function JourneyPage() {
  const { range, apiParams } = useDateRange();
  const rangeKey = useDateRangeKey();
  const [userIdInput, setUserIdInput] = useState('');
  const [visitorIdInput, setVisitorIdInput] = useState('');
  const [searchUserId, setSearchUserId] = useState<number | null>(null);
  const [searchVisitorId, setSearchVisitorId] = useState<string | null>(null);

  const funnel = useQuery<FunnelResponse>({
    queryKey: ['journey.funnel', rangeKey],
    queryFn: async () =>
      (await api().get('/journey/funnel', { params: apiParams })).data,
    refetchInterval: 60_000,
  });

  const timeline = useQuery<JourneyResponse>({
    queryKey: ['journey.timeline', searchUserId, searchVisitorId, rangeKey],
    queryFn: async () => {
      const params: Record<string, string | number> = { ...apiParams, limit: 500 };
      if (searchUserId !== null) params.user_id = searchUserId;
      if (searchVisitorId) params.visitor_id = searchVisitorId;
      const r = await api().get('/journey/timeline', { params });
      return r.data;
    },
    enabled: searchUserId !== null || !!searchVisitorId,
  });

  function handleSearch() {
    if (userIdInput.trim()) {
      const n = parseInt(userIdInput.trim(), 10);
      if (!isNaN(n)) {
        setSearchUserId(n);
        setSearchVisitorId(null);
        return;
      }
    }
    if (visitorIdInput.trim()) {
      setSearchVisitorId(visitorIdInput.trim());
      setSearchUserId(null);
    }
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <Title>Journey</Title>
        <Text>
          Funil de aquisição da plataforma + timeline detalhado de cada usuário/visitante.
        </Text>
      </header>

      <Card>
        <Title className="text-base">
          Funil de aquisição · {formatRangeShort(range)}
        </Title>
        {funnel.isLoading && <LoadingSkeleton rows={3} />}
        {funnel.error && (
          <Text className="text-red-400">
            {extractErrorMessage(funnel.error, 'erro ao carregar funil')}
          </Text>
        )}
        {funnel.data && (
          <div className="mt-4 space-y-2">
            {!!funnel.data.assistidos && (
              <Text className="text-[11px] text-amber-500">
                + {funnel.data.assistidos} cadastro(s) assistido(s) fora da contagem — conta aberta por nós para o prospect; vira adquirido no 1º login dele.
              </Text>
            )}
            {funnel.data.steps.map((s, idx) => {
              const widthPct = s.rate_from_top !== null ? Math.max(2, s.rate_from_top * 100) : 100;
              return (
                <div
                  key={s.key}
                  className="rounded border border-slate-800 bg-slate-900/40 p-3"
                >
                  <div className="flex items-baseline justify-between">
                    <div className="flex items-center gap-2">
                      <span className="rounded bg-slate-800 px-1.5 py-0.5 text-xs text-slate-400">
                        {idx + 1}
                      </span>
                      <span className="text-sm font-medium text-slate-200">
                        {s.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-slate-100">
                        {s.count.toLocaleString('pt-BR')}
                      </span>
                      {idx > 0 && (
                        <Badge color="blue" size="xs">
                          {fmtPct(s.rate_from_previous)} ↗ anterior
                        </Badge>
                      )}
                      <Badge color="violet" size="xs">
                        {fmtPct(s.rate_from_top)} do topo
                      </Badge>
                    </div>
                  </div>
                  <div className="mt-2 h-2 w-full rounded bg-slate-800">
                    <div
                      className="h-2 rounded bg-blue-500/60"
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card>
        <div className="flex items-center gap-2">
          <Footprints className="h-4 w-4 text-amber-300" />
          <Title className="text-base">Timeline de um usuário</Title>
        </div>
        <Text className="mt-1 text-xs text-slate-400">
          Buscar por user_id (numérico) ou visitor_id (string)
        </Text>
        <Grid numItems={3} className="mt-3 gap-2">
          <TextInput
            placeholder="user_id (ex: 42)"
            value={userIdInput}
            onChange={(e) => setUserIdInput(e.target.value)}
          />
          <TextInput
            placeholder="visitor_id"
            value={visitorIdInput}
            onChange={(e) => setVisitorIdInput(e.target.value)}
          />
          <Button icon={Search} onClick={handleSearch} disabled={!userIdInput && !visitorIdInput}>
            Buscar
          </Button>
        </Grid>

        {timeline.isLoading && <LoadingSkeleton rows={5} />}
        {timeline.error && (
          <Text className="mt-3 text-red-400">
            {extractErrorMessage(timeline.error, 'erro ao carregar timeline')}
          </Text>
        )}
        {timeline.data && (
          <div className="mt-4 space-y-1">
            <Text className="text-xs text-slate-400">
              {timeline.data.total_events} eventos em {timeline.data.sessions.length} sessões
            </Text>
            {timeline.data.sessions.map((s) => (
              <details
                key={s.session_id}
                className="rounded border border-slate-800 bg-slate-900/40"
              >
                <summary className="cursor-pointer px-3 py-2 text-sm">
                  <span className="text-slate-200">{fmtTime(s.started_at)}</span>
                  <span className="ml-3 text-slate-500">
                    {s.device_type} · {s.browser} · {s.os} · {s.country}
                  </span>
                  <span className="ml-3 text-slate-400">
                    {s.events.length} eventos
                  </span>
                  {s.utm_source && (
                    <Badge color="emerald" size="xs" className="ml-3">
                      utm: {s.utm_source}/{s.utm_medium}/{s.utm_campaign}
                    </Badge>
                  )}
                </summary>
                <div className="space-y-1 px-3 pb-3 pt-1">
                  {s.events.map((e) => (
                    <div
                      key={e.id}
                      className="flex items-center gap-2 border-l-2 border-slate-700 pl-2 text-xs"
                    >
                      <span className="rounded bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] text-slate-300">
                        {e.event_type}
                      </span>
                      {e.event_name && (
                        <span className="text-slate-200">{e.event_name}</span>
                      )}
                      {e.page_url && (
                        <span className="truncate text-slate-500">
                          {e.page_url}
                        </span>
                      )}
                      <span className="ml-auto text-slate-500">
                        {fmtTime(e.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
